<script setup>
import { computed, useAttrs, useSlots } from 'vue';
import { Link } from '@inertiajs/vue3';
import SpinnerIcon from '@/components/icons/SpinnerIcon.vue';

const props = defineProps({
    label: {
        type: String,
        default: null,
    },
    type: {
        type: String,
        default: null,
    },
    href: {
        type: String,
        default: undefined,
    },
    external: {
        type: Boolean,
        default: false,
    },
    icon: {
        type: [Function, Object],
        default: null,
    },
    iconPos: {
        type: String,
        default: 'left',
    },
    loading: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    size: {
        type: String,
        default: 'md',
        validator: value => ['xs', 'sm', 'md', 'lg'].includes(value),
    },
    variant: {
        type: String,
        default: 'primary',
        validator: value => [
            'primary',
            'secondary',
            'outline',
            'danger',
            'success',
            'warning',
            'gray',
            'link',
            'default',
        ].includes(value),
    },
    text: {
        type: Boolean,
        default: false,
    },
    rounded: {
        type: Boolean,
        default: false,
    },
});

const slots = useSlots();
const attrs = useAttrs();

const isDisabled = computed(() => props.disabled || props.loading);

const defaultAriaLabel = computed(() => props.label ?? attrs['aria-label']);

const hasIcon = computed(() => {
    return props.icon || slots.icon;
});

const defaultClasses = computed(() => {
    return [
        'inline-flex gap-2 cursor-pointer items-center justify-center font-medium whitespace-nowrap select-none transition ease-in-out duration-300 disabled:cursor-not-allowed disabled:opacity-60 disabled:pointer-events-none',
        {
            'flex-row-reverse': props.iconPos === 'right',
            'rounded-full!': props.rounded,
        },
    ];
});

const focusClasses = 'focus:outline-solid focus:outline-2 focus:outline-offset-0';

const sizeClasses = computed(() => {
    if (hasIcon.value && !props.label) {
        return [
            {
                'px-2 py-2 text-sm leading-normal rounded-lg': props.size === 'sm',
                'px-2.5 py-2.5 text-sm leading-normal rounded-lg h-10': props.size === 'md',
            },
        ];
    }

    return [
        {
            'px-2.5 py-1.5 text-xs leading-4 rounded-sm': props.size === 'xs',
            'px-3 py-1.5 text-sm leading-normal rounded-md': props.size === 'sm',
            'px-4 py-2 text-sm leading-normal rounded-md h-10': props.size === 'md',
            'px-4 py-2.5 text-base leading-normal rounded-md': props.size === 'lg',
        },
    ];
});

const variantClass = computed(() => {
    return {
        'bg-primary-500 text-white hover:bg-primary-700 focus:outline-primary-300':
            props.variant === 'primary',
        'bg-gray-100 text-gray-900 hover:bg-gray-400 active:bg-gray-400 focus:outline-gray-300':
            props.variant === 'secondary',
        'bg-transparent text-gray-900 hover:bg-gray-50 active:bg-gray-50 focus:outline-gray-300':
            props.variant === 'outline',
        'shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-red-500':
            props.variant === 'danger',
        'bg-green-500 text-white hover:bg-green-700 focus:outline-green-300':
            props.variant === 'success',
        'bg-yellow-500 text-white hover:bg-yellow-600 focus:outline-yellow-300':
            props.variant === 'warning',
        'border-transparent text-gray-700 bg-gray-200 border hover:text-gray-800 hover:bg-gray-300 focus:outline-gray-400':
            props.variant === 'gray',
        'bg-white border border-gray-300 text-gray-900 hover:border-gray-400 hover:bg-gray-100 hover:text-gray-900 active:bg-gray-200 shadow-xs focus:outline-gray-300':
            props.variant === 'default',
        'border-transparent bg-transparent text-primary hover:underline active:underline focus:outline-primary-300':
            props.variant === 'link',
        'bg-transparent!': props.text,
    };
});

const disabledClasses = computed(() => {
    if (isDisabled.value) {
        return 'focus:outline-hidden focus:ring-0';
    }

    return focusClasses;
});

const iconClasses = computed(() => {
    return {
        'size-3.5': props.size === 'xs',
        'size-4': props.size === 'sm' || props.size === 'md',
        'size-5': props.size === 'lg',
        '-ms-1': props.label && props.iconPos === 'left',
        '-me-1': props.label && props.iconPos === 'right',
    };
});

const iconVariantClasses = computed(() => {
    return {
        'opacity-60': props.label,
        'text-white': props.variant === 'primary' || props.variant === 'success' || props.variant === 'warning',
        'text-gray-600': props.variant === 'secondary',
        'text-gray-400': props.variant === 'gray',
        'text-gray-900': props.variant === 'default',
    };
});

const setType = computed(() => {
    if (!props.href) {
        return props.type ?? 'button';
    }

    return props.type;
});
</script>

<template>
    <component
        :is="external ? 'a' : href ? Link : 'button'"
        :href="href"
        :type="setType"
        :class="[defaultClasses, disabledClasses, sizeClasses, variantClass]"
        :aria-label="defaultAriaLabel"
        :disabled="isDisabled"
    >
        <slot
            v-if="loading"
            name="loadingIcon"
            :class="[iconClasses, iconVariantClasses]"
        >
            <SpinnerIcon :class="[iconClasses, iconVariantClasses]" />
        </slot>

        <slot
            v-else-if="icon"
            name="icon"
            :class="[iconClasses, iconVariantClasses]"
        >
            <component
                :is="icon"
                :class="[iconClasses, iconVariantClasses]"
            />
        </slot>

        <slot>
            <span v-if="label">{{ label }}</span>
        </slot>
    </component>
</template>
